// src/components/NotificationCenter/index.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Badge,
  Popover,
  List,
  Tag,
  Button,
  Divider,
  Empty,
  Spin,
  notification as antdNotification,
  Avatar,
  message
} from 'antd';
import {
  BellOutlined,
  ReloadOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import wsSingleton from '@/utils/wsSingleton';
import { notificationService } from '../service';
import { 
  NotificationType, 
  UserNotificationView,
  SystemType
} from '../data';
import './index.less';

// 使用 UserNotificationView 接口，不需要扩展
interface UserNotification extends UserNotificationView {
  // 移除 targetId，因为 UserNotificationView 已经包含 id
}

// 图标映射
const getNotificationIcon = (type: NotificationType) => {
  const icons = {
    [NotificationType.Inform]: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
    [NotificationType.Announcement]: <InfoCircleOutlined style={{ color: '#52c41a' }} />,
    [NotificationType.Expiration]: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
  };
  return icons[type] || <InfoCircleOutlined />;
};

// 类型颜色映射
const getTypeConfig = (type: NotificationType) => {
  const config = {
    [NotificationType.Inform]: { color: 'blue', text: '普通通知' },
    [NotificationType.Announcement]: { color: 'green', text: '公告' },
    [NotificationType.Expiration]: { color: 'red', text: '到期提醒' }
  };
  return config[type] || config[NotificationType.Inform];
};

// 系统类型文本映射
const getSystemTypeText = (systemType: SystemType): string => {
  switch (systemType) {
    case SystemType.All:
      return '全站';
    case SystemType.Website:
      return '网站系统';
    case SystemType.Software:
      return '软件系统';
    default:
      return '未知';
  }
};

// Ant Design 通知类型映射
const getNotificationAntdType = (type: NotificationType): 'info' | 'success' | 'error' | 'warning' => {
  const typeMap: Record<NotificationType, 'info' | 'success' | 'error' | 'warning'> = {
    [NotificationType.Inform]: 'info',
    [NotificationType.Announcement]: 'success',
    [NotificationType.Expiration]: 'error'
  };
  return typeMap[type] || 'info';
};

const NotificationCenter: React.FC = () => {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const initialized = useRef(false);

  // 获取当前用户ID
  const getCurrentUserId = (): number => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      return userInfo ? JSON.parse(userInfo).id : 0;
    } catch {
      return 0;
    }
  };

  useEffect(() => {
    if (!initialized.current) {
      initializeWebSocket();
      fetchNotifications();
      initialized.current = true;
    }

    return () => {
      // 清理 WebSocket 监听
      wsSingleton.off('new_notification', handleNewNotification);
      wsSingleton.off('notification_read', handleNotificationRead);
      wsSingleton.off('__internal_open', handleReconnect);
    };
  }, []);

  // 初始化 WebSocket
  const initializeWebSocket = () => {
    wsSingleton.on('new_notification', handleNewNotification);
    wsSingleton.on('notification_read', handleNotificationRead);
    wsSingleton.on('__internal_open', handleReconnect);
    subscribeToNotifications();
  };

  // 处理重连
  const handleReconnect = () => {
    console.log('[NotificationCenter] WebSocket 重连，重新订阅通知');
    subscribeToNotifications();
    fetchNotifications();
  };

  // 订阅通知
  const subscribeToNotifications = () => {
    const userId = getCurrentUserId();
    if (userId > 0) {
      wsSingleton.subscribeToNotifications(userId.toString());
    }
  };

  // 处理新通知
  const handleNewNotification = (newNotification: any) => {
    console.log('[NotificationCenter] 收到实时通知:', newNotification);
    
    if (!newNotification) {
      console.error('[NotificationCenter] 收到空的通知数据');
      return;
    }
    
    // 转换为 UserNotification 格式
    const userNotification: UserNotification = {
      id: newNotification.id || Date.now(),
      notificationId: newNotification.notificationId || newNotification.id,
      title: newNotification.title || '新通知',
      content: newNotification.content || '',
      type: newNotification.type || NotificationType.Inform,
      systemType: newNotification.systemType || SystemType.All,
      isRead: false,
      readAt: undefined,
      createdAt: newNotification.createdAt || new Date().toISOString(),
      // expireAt: newNotification.expireAt,
      // 移除 targetId，使用 id
    };
    
    // 添加到通知列表顶部
    setNotifications(prev => [userNotification, ...prev]);
    setUnreadCount(prev => prev + 1);

    // 显示桌面通知
    showDesktopNotification(userNotification);

    // 显示 Ant Design 通知提示
    if (!popoverVisible) {
      showAntdNotification(userNotification);
    }
  };

  // 显示 Ant Design 通知
  const showAntdNotification = (notification: UserNotification) => {
    const method = getNotificationAntdType(notification.type);
    
    const notificationConfig = {
      message: notification.title,
      description: (
        <div 
          onClick={() => {
            setPopoverVisible(true);
            markAsRead(notification.id); // 使用 id 而不是 targetId
          }}
          style={{ cursor: 'pointer' }}
        >
          {formatContent(notification.content, true)}
        </div>
      ),
      placement: 'topRight' as const,
      duration: 6,
    };

    // 使用类型安全的调用
    switch (method) {
      case 'success':
        antdNotification.success(notificationConfig);
        break;
      case 'error':
        antdNotification.error(notificationConfig);
        break;
      case 'warning':
        antdNotification.warning(notificationConfig);
        break;
      case 'info':
      default:
        antdNotification.info(notificationConfig);
        break;
    }
  };

  // 处理通知已读
  const handleNotificationRead = (data: { id: number; userId: string }) => {
    const currentUserId = getCurrentUserId();
    if (data.userId !== currentUserId.toString()) {
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === data.id ? { ...notif, isRead: true, readAt: new Date().toISOString() } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  // 显示桌面通知
  const showDesktopNotification = (notification: UserNotification) => {
    if (!('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: formatContent(notification.content, true),
        icon: '/favicon.ico',
        tag: notification.id.toString()
      });
    } else if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  // 格式化内容（移除HTML标签，限制长度）
  const formatContent = (content: string, plainText: boolean = false) => {
    if (!content) return '';
    
    if (plainText) {
      // 移除HTML标签并限制长度
      const cleanContent = content.replace(/<[^>]*>/g, '');
      return cleanContent.length > 100 ? cleanContent.substring(0, 100) + '...' : cleanContent;
    }
    
    // 对于HTML内容，直接返回（注意：在生产环境中应该对HTML进行清理）
    return content.length > 100 ? content.substring(0, 100) + '...' : content;
  };

  // 获取通知列表
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationService.getUserNotifications({
        skip: 0,
        take: 10,
        unreadOnly: false
      });
      
      if (response && response.list) {
        // 直接使用 UserNotificationView
        const userNotifications: UserNotification[] = response.list
          .filter((item: any) => item != null)
          .map((item: UserNotificationView) => ({
            ...item,
            // 不需要添加 targetId，因为 id 就是 NotificationByUser 的 id
          }));
        
        setNotifications(userNotifications);
        // setUnreadCount(response.unreadCount || 0);
      }
      
    } catch (error: any) {
      console.error('获取通知失败:', error);
      message.error('获取通知失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  // 标记为已读
  const markAsRead = async (id: number) => {
    try {
      // 乐观更新
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === id ? { ...notif, isRead: true, readAt: new Date().toISOString() } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));

      // 调用 Service
      await notificationService.markAsRead(id);
      
      // 通过 WebSocket 广播
      const userId = getCurrentUserId();
      // 注意：需要根据你的 wsSingleton 实际实现调整
      // wsSingleton.markNotificationAsRead(id.toString(), userId.toString());

    } catch (error: any) {
      console.error('标记已读失败:', error);
      // 回滚
      setNotifications(prev =>
        prev.map(notif =>
            notif.id === id ? { ...notif, isRead: false, readAt: undefined } : notif
          )
      );
      setUnreadCount(prev => prev + 1);
      message.error('标记已读失败');
    }
  };

  // 标记所有为已读
  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(notif => !notif.isRead);
    if (unreadNotifications.length === 0) {
      message.info('没有未读通知');
      return;
    }

    try {
      const ids = unreadNotifications.map(notif => notif.id);
      
      // 乐观更新
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);

      // 批量标记
      await notificationService.batchMarkAsRead({ ids });

      message.success(`已标记 ${unreadNotifications.length} 条通知为已读`);

    } catch (error: any) {
      console.error('标记所有已读失败:', error);
      // 重新获取数据
      fetchNotifications();
      message.error('标记所有已读失败');
    }
  };

  // 格式化时间
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '未知时间';
    
    try {
      const now = new Date();
      const time = new Date(timeStr);
      
      // 检查时间是否有效
      if (isNaN(time.getTime())) {
        return '无效时间';
      }
      
      const diff = now.getTime() - time.getTime();
      
      if (diff < 60000) return '刚刚';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;
      
      return time.toLocaleDateString();
    } catch (error) {
      return '无效时间';
    }
  };

  // 检查通知是否已过期
  const isNotificationExpired = (notification: UserNotification): boolean => {
    // if (!notification.expireAt) return false;
    // return new Date(notification.expireAt) < new Date();
    return false;
  };

  // 安全渲染通知项
  const renderNotificationItem = (item: UserNotification) => {
    // 检查 item 是否有效
    if (!item || typeof item !== 'object') {
      console.warn('跳过无效通知项:', item);
      return null;
    }

    try {
      const typeConfig = getTypeConfig(item.type);
      const isExpired = isNotificationExpired(item);
      
      return (
        <List.Item
          className={`notification-item ${item.isRead ? 'read' : 'unread'} ${isExpired ? 'expired' : ''}`}
          onClick={() => !item.isRead && markAsRead(item.id)}
          style={{ cursor: item.isRead ? 'default' : 'pointer' }}
        >
          <div className="notification-content">
            <div className="notification-item-header">
              <Avatar 
                size="small" 
                icon={getNotificationIcon(item.type)}
                style={{ marginRight: 8 }}
              />
              <span className="notification-item-title">{item.title || '无标题'}</span>
              <Tag color={typeConfig.color} >
                {typeConfig.text}
              </Tag>
              {isExpired && (
                <Tag color="red" style={{ marginLeft: 4 }}>
                  已过期
                </Tag>
              )}
              {!item.isRead && <div className="unread-dot" />}
            </div>
            <div 
              className="notification-item-body"
              dangerouslySetInnerHTML={{ __html: formatContent(item.content) }}
            />
            <div className="notification-item-meta">
              <div className="notification-item-time">
                <ClockCircleOutlined style={{ marginRight: 4, fontSize: 12 }} />
                {formatTime(item.createdAt)}
              </div>
              <div className="notification-item-system">
                {getSystemTypeText(item.systemType)}
              </div>
            </div>
          </div>
        </List.Item>
      );
    } catch (error) {
      console.error('渲染通知项时出错:', error, item);
      return (
        <List.Item>
          <div style={{ color: '#ff4d4f', padding: '12px 16px' }}>
            通知内容渲染出错
          </div>
        </List.Item>
      );
    }
  };

  const notificationContent = (
    <div className="notification-popover">
      <div className="notification-header">
        <span className="notification-title">通知中心</span>
        <div className="notification-actions">
          <Button 
            type="text" 
            icon={<ReloadOutlined />} 
            onClick={fetchNotifications}
            size="small"
            loading={loading}
          />
          <Button 
            type="text" 
            icon={<CheckOutlined />} 
            onClick={markAllAsRead}
            size="small"
            disabled={unreadCount === 0}
          >
            全部已读
          </Button>
        </div>
      </div>
      
      <Divider style={{ margin: '12px 0' }} />

      <div className="notification-list">
        {loading ? (
          <div className="notification-loading">
            <Spin size="small" />
            <span style={{ marginLeft: 8 }}>加载中...</span>
          </div>
        ) : notifications.length > 0 ? (
          <List
            dataSource={notifications}
            renderItem={renderNotificationItem}
            locale={{ emptyText: '暂无通知' }}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无通知" />
        )}
      </div>

      {notifications.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div className="notification-footer">
            <div className="notification-stats">
              <span>共 {notifications.length} 条通知</span>
              {unreadCount > 0 && (
                <span className="unread-count">未读: {unreadCount}</span>
              )}
            </div>
            <Button 
              type="link" 
              size="small" 
              onClick={() => {
                // 跳转到通知页面
                window.location.href = '/notifications';
              }}
            >
              查看全部通知
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Popover
      content={notificationContent}
      trigger="click"
      placement="bottomRight"
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
      overlayClassName="notification-popover-container"
    >
      <Badge 
        count={unreadCount} 
        size="small" 
        offset={[-5, 5]}
        className="notification-badge"
      >
        <div className="notification-trigger">
          <BellOutlined />
          {unreadCount > 0 && <div className="notification-pulse" />}
        </div>
      </Badge>
    </Popover>
  );
};

export default NotificationCenter;